import React, { useEffect, useState } from 'react'
import { inject, observer } from 'mobx-react'
import { Button, Col, Form, Row, Table } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import useTitle from 'hooks/useTitle'
import LoadingSpinner from 'Components/LoadingSpinner'
import apiService from 'Service'
import companyStore from 'stores/companyStore'

const subjectTypes = [
  'PersonaNaturalDomiciliada',
  'PersonaJuridicaDomiciliada',
  'PersonaNaturalNoDomiciliada',
  'PersonaJuridicaNoDomiciliada'
]

const emptyForm = {
  ConceptCode: '',
  ConceptDescription: '',
  SubjectType: 'PersonaNaturalDomiciliada',
  RatePercentage: 34,
  SubtrahendUt: ''
}

const IslrRulesPage = ({ uiStore }) => {
  const { t } = useTranslation()
  useTitle('islr_rules')
  const [rules, setRules] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [editId, setEditId] = useState(null)
  const countryId = companyStore.selectedCompany?.IdCountry || 1

  const load = () => {
    setLoading(true)
    apiService.IslrRules.list(countryId)
      .then(data => setRules(Array.isArray(data) ? data : []))
      .catch(() => uiStore.addNotification({
        title: 'Error',
        message: 'No se pudieron cargar las reglas ISLR.',
        type: 'danger'
      }))
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [countryId])

  const onChange = e => {
    const { name, value } = e.target
    setForm(prev => ({
      ...prev,
      [name]: name === 'RatePercentage' || name === 'SubtrahendUt'
        ? (value === '' ? '' : Number(value))
        : value
    }))
  }

  const onSubmit = e => {
    e.preventDefault()
    setSaving(true)
    const payload = {
      CountryId: countryId,
      ConceptCode: form.ConceptCode,
      ConceptDescription: form.ConceptDescription,
      SubjectType: form.SubjectType,
      RatePercentage: Number(form.RatePercentage),
      SubtrahendUt: form.SubtrahendUt === '' ? null : Number(form.SubtrahendUt)
    }
    const action = editId
      ? apiService.IslrRules.update(editId, payload)
      : apiService.IslrRules.create(payload)
    action
      .then(() => {
        setForm(emptyForm)
        setEditId(null)
        load()
        uiStore.addNotification(uiStore.appNotifications.SUCCESS_SETTINGS_CHANGED)
      })
      .finally(() => setSaving(false))
  }

  const onEdit = rule => {
    setEditId(rule.Id)
    setForm({
      ConceptCode: rule.ConceptCode,
      ConceptDescription: rule.ConceptDescription,
      SubjectType: rule.SubjectType,
      RatePercentage: rule.RatePercentage,
      SubtrahendUt: rule.SubtrahendUt ?? ''
    })
  }

  const onDelete = async id => {
    const confirmed = await uiStore.confirm({
      title: t('confirm_delete_title'),
      message: t('deactivate_islr_rule'),
      confirmLabel: t('delete'),
      cancelLabel: t('cancel'),
      variant: 'danger',
    })
    if (!confirmed) return
    apiService.IslrRules.delete(id).then(() => load())
  }

  if (loading) return <LoadingSpinner />

  return (
    <div>
      <h4>Catálogo ISLR (Art. 9)</h4>
      <p className="text-muted">País: {countryId}. Solo owner o administrador de compañía invitada.</p>

      <Form onSubmit={onSubmit} className="mb-4 border rounded p-3 bg-light">
        <Row>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Código</Form.Label>
              <Form.Control name="ConceptCode" value={form.ConceptCode} onChange={onChange} required />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Descripción</Form.Label>
              <Form.Control name="ConceptDescription" value={form.ConceptDescription} onChange={onChange} required />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Sujeto</Form.Label>
              <Form.Control as="select" name="SubjectType" value={form.SubjectType} onChange={onChange}>
                {subjectTypes.map(s => <option key={s} value={s}>{s}</option>)}
              </Form.Control>
            </Form.Group>
          </Col>
          <Col md={1}>
            <Form.Group>
              <Form.Label>%</Form.Label>
              <Form.Control type="number" step="0.01" name="RatePercentage" value={form.RatePercentage} onChange={onChange} required />
            </Form.Group>
          </Col>
          <Col md={1}>
            <Form.Group>
              <Form.Label>Sub UT</Form.Label>
              <Form.Control type="number" step="0.01" name="SubtrahendUt" value={form.SubtrahendUt} onChange={onChange} />
            </Form.Group>
          </Col>
        </Row>
        <Button type="submit" disabled={saving}>{editId ? 'Actualizar' : 'Agregar'}</Button>
        {editId && (
          <Button variant="secondary" className="ml-2" onClick={() => { setEditId(null); setForm(emptyForm) }}>
            Cancelar
          </Button>
        )}
      </Form>

      <Table striped bordered hover size="sm">
        <thead>
          <tr>
            <th>Concepto</th>
            <th>Descripción</th>
            <th>Sujeto</th>
            <th>%</th>
            <th>Sub. UT</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rules.map(rule => (
            <tr key={rule.Id}>
              <td>{rule.ConceptCode}</td>
              <td>{rule.ConceptDescription}</td>
              <td>{rule.SubjectType}</td>
              <td>{rule.RatePercentage}</td>
              <td>{rule.SubtrahendUt ?? '-'}</td>
              <td className="text-nowrap">
                <Button size="sm" variant="outline-primary" className="mr-1" onClick={() => onEdit(rule)}>Editar</Button>
                <Button size="sm" variant="outline-danger" onClick={() => onDelete(rule.Id)}>Desactivar</Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  )
}

export default inject('uiStore')(observer(IslrRulesPage))
